/*
 * Copyright (c) 2023. All rights are reserved to Newport Collection
 */

import { esales } from '@apptus/esales-api';
import SessionStore from '@services/stores/SessionStore';

function getDiscoveryKey() {
    const apptusSession = getUserSession();
    const sessionStateMethods = SessionStore.getState()?.methods;

    return sessionStateMethods?.getEclubData()?.discoveryKey // primary
        || sessionStateMethods?.getEclubData?.contactId // fallback
        || apptusSession?.customerKey; // probably logged out
}

export function getUserSession() {
    return localStorage.getItem('apptus.session') ? JSON.parse(localStorage.getItem('apptus.session')) : false;
}

export async function elevateSearch(q, sort, facets, page, market, locale, touchpoint, soldOut = false) {
    const clusterId = process.env.NEXT_PUBLIC_VOYADO_CLUSTER_ID || 'wD1503A7C';
    const limit = process.env.VOYADO_SEARCH_LIMIT || 48;
    const filterableFields = ['price', 'department', 'color', 'brand'];
    const filterableFacets = {};
    Object.keys(facets).forEach((facetKey) => {
        if(filterableFields.indexOf(facetKey) >= 0) {
            filterableFacets[facetKey] = facets[facetKey];
        }
    });

    let searchResults;

    if(locale.indexOf('en-') >= 0) {
        locale = 'en-IE';
    }

    const api = esales({
        market,
        locale,
        clusterId,
        touchpoint
    });

    if(getDiscoveryKey()) {
        api.session.customerKey = getDiscoveryKey();
    }

    if(soldOut) {
        searchResults = await api.query.landingPage({
            pageReference: '/sold-out-products',
            sort,
            facets: filterableFacets,
            skip: (page > 1 ? limit * (page - 1) : 0),
            limit,
            notify: false,
            presentCustom: 'variant.uuid, variant.in_stock, variant.fast_delivery_available, variant.marketing_label'
        });
    } else {
        searchResults = await api.query.searchPage({
            q,
            sort,
            facets: filterableFacets,
            skip: (page > 1 ? limit * (page - 1) : 0),
            limit,
            presentCustom: 'variant.uuid, variant.in_stock, variant.fast_delivery_available, variant.marketing_label'
        });
    }

    return searchResults;
}

export async function elevateAutocomplete(q, market, locale, touchpoint) {
    const clusterId = process.env.NEXT_PUBLIC_VOYADO_CLUSTER_ID || 'wD1503A7C';

    if(locale.indexOf('en-') >= 0) {
        locale = 'en-IE';
    }

    const api = esales({
        market,
        locale,
        clusterId,
        touchpoint
    });

    if(getDiscoveryKey()) {
        api.session.customerKey = getDiscoveryKey();
    }

    const autocompleteResults = await api.query.autocomplete({
        q,
        presentCustom: 'variant.uuid, variant.in_stock, variant.fast_delivery_available, variant.marketing_label'
    });

    return autocompleteResults;
}

export async function elevateCart(cart, market, locale, touchpoint) {

    // BARE HTTP REQ UNTIL WE CAN UPGRADE THE ELEVATE-PACKAGE (NODE -v >= 18)
    const clusterId = process.env.NEXT_PUBLIC_VOYADO_CLUSTER_ID || 'wD1503A7C';

    if (locale.indexOf('en-') >= 0) {
        locale = 'en-IE';
    }
    const params = {
        market,
        locale,
        clusterId,
        touchpoint,
        cart: cart.toString()?.replaceAll(',', '|'),
        channels: 'ONLINE',
        presentCustom: 'variant.uuid, variant.in_stock, variant.fast_delivery_available, variant.marketing_label',
        variantKey: cart[cart.length-1],
    }

    const body = {
        recommendationLists: [
            {
                id: 'UPSELL',
                algorithm: 'ADD_TO_CART_RECS',
                limit: 4
            }
        ]}


    const apptusSession = getUserSession();
    params.sessionKey = apptusSession.sessionKey;
    params.customerKey = apptusSession.customerKey;

    if (getDiscoveryKey()) {
        params.customerKey = getDiscoveryKey();
    }

    const url = `https://${clusterId}.api.esales.apptus.cloud/api/storefront/v3/queries/add-to-cart-popup?` + new URLSearchParams(params).toString();
    let content = {};
    try {
        const res = await fetch(url, {
            method: "POST",
            body: JSON.stringify(body)
        });
        content = res.json();
    } catch(e) {
        content.recommendationLists = []
        console.error(e)
    }

    return content;
}

export async function elevateCheckout(cart, market, locale, touchpoint) {
    const clusterId = process.env.NEXT_PUBLIC_VOYADO_CLUSTER_ID || 'wD1503A7C';

    if(locale.indexOf('en-') >= 0) {
        locale = 'en-IE';
    }

    const api = esales({
        market,
        locale,
        clusterId,
        touchpoint
    });

    if(getDiscoveryKey()) {
        api.session.customerKey = getDiscoveryKey();
    }

    try {
        const recommendationResults = await api.query.cartPage({ cart, presentCustom: 'variant.uuid, variant.in_stock, variant.fast_delivery_available, variant.marketing_label'}, {
            recommendationLists: [
                {
                    id: 'UPSELL',
                    algorithm: 'CART',
                    limit: 6
                }
            ]
        });

        const anniProductResults = await api.query.landingPage({ pageReference: '/anniversary-products', presentCustom: 'variant.uuid, variant.in_stock, variant.fast_delivery_available, variant.marketing_label'});

        recommendationResults.recommendationLists.unshift(anniProductResults.primaryList);
        return recommendationResults;
    } catch(e) {
        return {recommendationLists: []}
    }
}

export async function elevateLandingPage(landingPage, sort, facets, page, market, locale, touchpoint) {
    const clusterId = process.env.NEXT_PUBLIC_VOYADO_CLUSTER_ID || 'wD1503A7C';
    const limit = process.env.VOYADO_SEARCH_LIMIT || 12;
    const filterableFields = ['price', 'department', 'color', 'brand'];
    const filterableFacets = {};
    Object.keys(facets).forEach((facetKey) => {
        if(filterableFields.indexOf(facetKey) >= 0) {
            filterableFacets[facetKey] = facets[facetKey];
        }
    });

    if(locale.indexOf('en-') >= 0) {
        locale = 'en-IE';
    }

    const api = esales({
        market,
        locale,
        clusterId,
        touchpoint
    });

    if(getDiscoveryKey()) {
        api.session.customerKey = getDiscoveryKey();
    }

    return await api.query.landingPage({
        pageReference: landingPage,
        sort,
        facets: filterableFacets,
        skip: (page > 1 ? limit * (page - 1) : 0),
        limit,
        presentCustom: 'variant.uuid, variant.in_stock, variant.fast_delivery_available, variant.marketing_label'
    });
}

export async function elevateClickNotification(ticket, market, locale, touchpoint) {
    const clusterId = process.env.NEXT_PUBLIC_VOYADO_CLUSTER_ID || 'wD1503A7C';

    if(locale.indexOf('en-') >= 0) {
        locale = 'en-IE';
    }

    const api = esales({
        market,
        locale,
        clusterId,
        touchpoint
    });

    if(getDiscoveryKey()) {
        api.session.customerKey = getDiscoveryKey();
    }

    await api.notify.click(ticket);
}

export async function elevateAddToCartNotification(ticket, market, locale, touchpoint) {
    const clusterId = process.env.NEXT_PUBLIC_VOYADO_CLUSTER_ID || 'wD1503A7C';

    if(locale.indexOf('en-') >= 0) {
        locale = 'en-IE';
    }

    const api = esales({
        market,
        locale,
        clusterId,
        touchpoint
    });

    if(getDiscoveryKey()) {
        api.session.customerKey = getDiscoveryKey();
    }

    await api.notify.addToCart(ticket);
}

export async function elevateStyleWith(productKey, market, locale, touchpoint) {
    const clusterId = process.env.NEXT_PUBLIC_VOYADO_CLUSTER_ID || 'wD1503A7C';

    if(locale.indexOf('en-') >= 0) {
        locale = 'en-IE';
    }

    const api = esales({
        market,
        locale,
        clusterId,
        touchpoint
    });

    if(getDiscoveryKey()) {
        api.session.customerKey = getDiscoveryKey();
    }

    return await api.query.productPage({
        productKey: productKey,
        presentCustom: 'variant.uuid, variant.in_stock, variant.fast_delivery_available, variant.marketing_label',
    }, {
        recommendationLists: [
            {
                id: 'styleWith',
                algorithm: 'STYLE_WITH',
                limit: 3,
                params: {
                    productKey: productKey
                }
            },
        ]
    });
}

export async function elevateGeneralRecommendations(algorithm, limit, market, locale, touchpoint) {
    const clusterId = process.env.NEXT_PUBLIC_VOYADO_CLUSTER_ID || 'wD1503A7C';

    if(locale.indexOf('en-') >= 0) {
        locale = 'en-IE';
    }

    const api = esales({
        market,
        locale,
        clusterId,
        touchpoint
    });

    if(getDiscoveryKey()) {
        api.session.customerKey = getDiscoveryKey();
    }

    return await api.query.landingPage({
        pageReference: '/',
        presentCustom: 'variant.uuid, variant.in_stock, variant.fast_delivery_available, variant.marketing_label',
    }, {
        recommendationLists: [
            {
                id: 'recommendations',
                algorithm,
                limit
            },
        ]
    });
}

export async function elevateProductPage(productKey, market, locale, touchpoint) {
    const clusterId = process.env.NEXT_PUBLIC_VOYADO_CLUSTER_ID || 'wD1503A7C';

    if(locale.indexOf('en-') >= 0) {
        locale = 'en-IE';
    }

    const api = esales({
        market,
        locale,
        clusterId,
        touchpoint
    });

    if(getDiscoveryKey()) {
        api.session.customerKey = getDiscoveryKey();
    }

    return await api.query.productPage({
        productKey: productKey,
        presentCustom: 'variant.uuid, variant.in_stock, variant.fast_delivery_available, variant.marketing_label',
    }, {
        recommendationLists: [
            {
                id: 'PDP-1',
                algorithm: 'ALTERNATIVES',
                limit: 16,
                params: {
                    productKey: productKey
                }
            },
            {
                id: 'PDP-2',
                algorithm: 'UPSELL',
                limit: 16,
                params: {
                    productKey: productKey
                }
            },
            {
                id: 'PDP-3',
                algorithm: 'RECENTLY_VIEWED',
                limit: 16
            }
        ]
    });
}
